/**
 * 「見張り役」のカード（ADR-0052、Phase 3-5）。カード欄の一番上に出す:
 *   - 凍結中の帯（いつから・なぜ・解き方）
 *   - 見張り役が止まっている（開いたときに Gateway が最後の見回りの時刻から出す。cron は使わない）
 *   - この時間の警告と、止めている能力
 *   - 「止める」ボタン（キルスイッチ、13.5。パスキーの端末だけ。解くボタンは無い）
 * 凍結中・見張り役が止まっている間だけ、設定の間隔（config/client.yaml の monitor.refresh_sec）で描き直す。
 * 声の端末・未適用の環境では出ない（Gateway が 403・503 を返す）。
 */
import { ApiError, api } from "./api.js";
import { shieldIcon, stopIcon } from "./icons.js";
import {
  FREEZE_CONFIRM,
  freezeErrorText,
  type MonitorView,
  monitorCardModel,
} from "./monitor-model.js";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  // 文字列は Text ノードとして入れる（警告の文・理由を HTML として解釈しない）
  e.append(...children);
  return e;
}

const timers = new WeakMap<HTMLElement, number>();

export interface MonitorCardOptions {
  /** 止めた後に、ほかのカード（承認待ち・作業係・今日のこと）を描き直す */
  onFrozen?: () => void;
  /** 「止める」ボタンの置き場（引き出しの一番下。ADR-0055）。無ければカードの中 */
  freezeSlot?: HTMLElement;
  /** 読めたとき、凍結・見張り役の様子を知らせる（見出しの状態の行・数の札に使う） */
  onView?: (v: MonitorView) => void;
}

/** 開いた画面（引き出し）に出す「見張り役」のカード。onFrozen は MonitorCardOptions の形でも受ける */
export async function renderMonitorCard(
  root: HTMLElement,
  arg: (() => void) | MonitorCardOptions = {},
): Promise<void> {
  const opts: MonitorCardOptions = typeof arg === "function" ? { onFrozen: arg } : arg;
  const onFrozen = opts.onFrozen;
  const prev = timers.get(root);
  if (prev !== undefined) clearTimeout(prev);
  let view: MonitorView;
  try {
    view = await api<MonitorView>("/v1/monitor");
  } catch (e) {
    if (!(e instanceof ApiError)) console.warn(e);
    root.replaceChildren();
    opts.freezeSlot?.replaceChildren();
    return;
  }
  opts.onView?.(view);
  const m = monitorCardModel(view);
  const status = el("p", { class: "muted small" });
  const parts: (Node | string)[] = [];
  if (m.banner)
    parts.push(
      el(
        "div",
        { class: "frozen-banner", role: "status" },
        el("p", { class: "frozen-title" }, m.banner.title),
        el("p", { class: "small" }, m.banner.reason),
        el("p", { class: "small muted" }, m.banner.help),
      ),
    );
  parts.push(
    el("p", { class: `small${m.status.down ? " monitor-down" : " muted"}` }, m.status.text),
  );
  for (const a of m.alerts)
    parts.push(el("p", { class: `small monitor-alert ${a.item.severity}` }, a.text));
  if (m.moreLine) parts.push(el("p", { class: "small muted" }, m.moreLine));
  if (m.blockedLine) parts.push(el("p", { class: "small" }, m.blockedLine));
  opts.freezeSlot?.replaceChildren();
  if (m.canFreeze) {
    // 止める口: 理由（任意）は押してから聞く（引き出しの一番下に置くので、入力欄は常には出さない）。
    // 色は琥珀（本人の判断で止める側。赤ではない）
    const btn = el(
      "button",
      { class: "freeze", type: "button" },
      stopIcon(),
      "Jarvis を止める",
    ) as HTMLButtonElement;
    btn.onclick = async () => {
      if (!window.confirm(FREEZE_CONFIRM)) return;
      const typed = window.prompt(`止める理由（任意、${view.reason_max_chars} 字まで）`, "");
      if (typed === null) return;
      btn.disabled = true;
      try {
        await api("/v1/monitor/freeze", { reason: typed.trim().slice(0, view.reason_max_chars) });
        onFrozen?.();
        await renderMonitorCard(root, opts);
      } catch (e) {
        const code = e instanceof ApiError ? e.code : String(e);
        status.textContent = freezeErrorText(code, view.reason_max_chars);
        btn.disabled = false;
      }
    };
    if (opts.freezeSlot) opts.freezeSlot.append(btn);
    else parts.push(el("div", { class: "row" }, btn));
  }
  root.replaceChildren(
    el(
      "section",
      { class: `card monitor${m.banner ? " frozen" : ""}` },
      el("h2", {}, m.heading),
      ...parts,
      status,
    ),
  );
  if (m.refreshMs !== null)
    timers.set(
      root,
      window.setTimeout(() => {
        if (document.visibilityState === "visible") void renderMonitorCard(root, opts);
        else
          document.addEventListener("visibilitychange", () => void renderMonitorCard(root, opts), {
            once: true,
          });
      }, m.refreshMs),
    );
}

/**
 * 会話の流れの上に出す短い知らせ（Attention の画面。ADR-0055）。ふだんは出ない。出るのは凍結中・見張り役が止まっている・
 * この時間の警告があるとき。文は monitor-model の同じ行（事実は全て応答から）。全部は引き出しの「見張り役」で
 */
export async function renderMonitorNotice(root: HTMLElement, onView?: (v: MonitorView) => void) {
  let view: MonitorView;
  try {
    view = await api<MonitorView>("/v1/monitor");
  } catch (e) {
    if (!(e instanceof ApiError)) console.warn(e);
    root.replaceChildren();
    return;
  }
  onView?.(view);
  const m = monitorCardModel(view);
  const lines: { text: string; sub: string; level: "critical" | "warn" | "info" }[] = [];
  if (m.banner)
    lines.push({
      text: m.banner.title,
      sub: `${m.banner.reason} · 会話と読み取りは続けられます`,
      level: "critical",
    });
  else if (m.status.down)
    lines.push({ text: m.status.text, sub: "下の取っ手から「見張り役」を開けます", level: "warn" });
  else if (m.alerts[0]) {
    const a = m.alerts[0];
    lines.push({
      text: `見張り役: ${a.item.label}${a.item.detail ? `。${a.item.detail}` : ""}`,
      sub: `${a.item.at}${a.item.until ? ` · ${a.item.until} まで止めています` : ""} · 会話は続けられます${m.moreLine ? ` · ${m.moreLine}` : ""}`,
      level: a.item.severity === "critical" ? "critical" : "warn",
    });
  }
  const first = lines[0];
  if (!first) {
    root.replaceChildren();
    return;
  }
  root.replaceChildren(
    el(
      "div",
      { class: `jv-notice ${first.level}`, role: "status" },
      shieldIcon(),
      el(
        "div",
        { class: "jv-notice-body" },
        first.text,
        el("div", { class: "jv-notice-sub" }, first.sub),
      ),
    ),
  );
}
